import { CircleArrowLeft02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { notify } from '@pique/component';
import { getCurrentStore } from '@toeverything/infra';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import { useAtom, useAtomValue } from 'jotai';
import { ArrowRight, X } from 'lucide-react';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { CallBackProps, Step } from 'react-joyride';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';

import { AIProvider } from '../../blocksuite/presets/ai/provider';
import {
  appSidebarOpenAtom,
  appSidebarWidthAtom,
  isMobileView,
} from '../../components/app-sidebar/index.jotai';
import {
  hideProTipSlot,
  showProTipSlot,
} from '../../components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';
import {
  getPostAugmentationSteps,
  postAugmentationStepIndexAtom,
} from './atoms';
import { CustomBeacon } from './custom-beacon';
import { CustomTooltip } from './custom-tooltip';
import { useOnboardingContext } from './onboarding-context';
import * as styles from './post-augmentation-tour.css';
import { DEFAULT_JOYRIDE_PROPS, STORAGE_KEYS } from './types';

const NOTIFICATION_DURATION_MS = 5000;
const ACTION_ITEMS_OVERVIEW_DELAY_MS = 500;
const FINAL_MODAL_DELAY_MS = 800;

function highlightTourButton(
  duration: number = NOTIFICATION_DURATION_MS
): void {
  const button = document.querySelector(
    '[data-testid="slider-bar-tour-button"]'
  ) as HTMLElement;
  if (!button) return;

  const originalBg = button.style.backgroundColor;
  button.style.backgroundColor = 'rgba(140, 92, 245, 0.12)';
  button.style.borderRadius = '8px';

  setTimeout(() => {
    button.style.backgroundColor = originalBg;
  }, duration);
}

interface PostAugmentationTourContextType {
  showCompletionModal: boolean;
  triggerPostAugmentationTour: () => void;
  handleCompletionModalClose: () => void;
  isRunning: boolean;
  stepIndex: number;
  persistedStepIndex: number;
  resumePostAugmentationTour: (fromStep?: number) => void;
  handleJoyrideCallback: (data: CallBackProps) => void;
  currentStepId: string | null;
  advanceToNextStep: () => void;
}

const PostAugmentationTourContext =
  createContext<PostAugmentationTourContextType | null>(null);

export const usePostAugmentationTour = () => {
  const context = useContext(PostAugmentationTourContext);
  if (!context) {
    throw new Error(
      'usePostAugmentationTour must be used within PostAugmentationTourProvider'
    );
  }
  return context;
};

interface CompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
}

const CompletionModal = ({ isOpen, onClose, onSkip }: CompletionModalProps) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose]);

  const handleSkip = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onSkip();
      setIsClosing(false);
    }, 200);
  }, [onSkip]);

  const overlayVariants: Variants = {
    visible: {
      opacity: 1,
      transition: { duration: 0.2, ease: 'easeOut' },
    },
    hidden: {
      opacity: 0,
      transition: { duration: 0.15, ease: 'easeIn' },
    },
  };

  const containerVariants: Variants = {
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
    },
    hidden: {
      scale: 0.96,
      opacity: 0,
      y: 8,
      transition: { duration: 0.15, ease: [0.4, 0, 1, 1] },
    },
  };

  if (!isOpen && !isClosing) return null;

  return (
    <AnimatePresence>
      {(isOpen || isClosing) && (
        <motion.div
          className={styles.overlay}
          variants={overlayVariants}
          initial="hidden"
          animate={isClosing ? 'hidden' : 'visible'}
          exit="hidden"
        >
          <motion.div
            className={styles.modalContainer}
            variants={containerVariants}
            initial="hidden"
            animate={isClosing ? 'hidden' : 'visible'}
            exit="hidden"
          >
            <button className={styles.closeButton} onClick={handleSkip}>
              <X size={18} />
            </button>

            <div className={styles.content}>
              <h2 className={styles.title}>Your Notes Are Ready! 🎉</h2>
              <p className={styles.description}>
                Pique has created an interactive mind map and structured notes
                with timestamps.
              </p>
            </div>

            <button className={styles.ctaButton} onClick={handleClose}>
              Let's go!
              <ArrowRight size={16} />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface FinalCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
}

const FinalCompletionModal = ({
  isOpen,
  onClose,
  userName,
}: FinalCompletionModalProps) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose]);

  const overlayVariants: Variants = {
    visible: {
      opacity: 1,
      transition: { duration: 0.2, ease: 'easeOut' },
    },
    hidden: {
      opacity: 0,
      transition: { duration: 0.15, ease: 'easeIn' },
    },
  };

  const containerVariants: Variants = {
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
    },
    hidden: {
      scale: 0.96,
      opacity: 0,
      y: 8,
      transition: { duration: 0.15, ease: [0.4, 0, 1, 1] },
    },
  };

  if (!isOpen && !isClosing) return null;

  const displayName = userName ? `, ${userName}` : '';

  return (
    <AnimatePresence>
      {(isOpen || isClosing) && (
        <motion.div
          className={styles.overlay}
          variants={overlayVariants}
          initial="hidden"
          animate={isClosing ? 'hidden' : 'visible'}
          exit="hidden"
        >
          <motion.div
            className={styles.modalContainer}
            variants={containerVariants}
            initial="hidden"
            animate={isClosing ? 'hidden' : 'visible'}
            exit="hidden"
          >
            <button className={styles.closeButton} onClick={handleClose}>
              <X size={18} />
            </button>

            <div className={styles.content}>
              <h2 className={styles.title}>
                Congratulations{displayName}! You just completed your tour of
                Pique! 👏
              </h2>
              <p className={styles.description}>
                If you ever want a refresher, you can replay this onboarding
                from the Help menu.
              </p>
            </div>

            <button className={styles.ctaButton} onClick={handleClose}>
              Finish Onboarding ✅
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface PostAugmentationTourProviderProps {
  children: React.ReactNode;
}

export const PostAugmentationTourProvider: React.FC<
  PostAugmentationTourProviderProps
> = ({ children }) => {
  const {
    triggerPermissionsDock,
    isRunning: isMainTourRunning,
    showSurvey,
  } = useOnboardingContext();
  const isMobileViewValue = useAtomValue(isMobileView);
  const [persistedStepIndex, setPersistedStepIndex] = useAtom(
    postAugmentationStepIndexAtom
  );
  const [onboardingStateVersion, setOnboardingStateVersion] = useState(0);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const stepIndexRef = useRef(stepIndex);
  stepIndexRef.current = stepIndex;
  const [showFinalModal, setShowFinalModal] = useState(false);
  const [userName, setUserName] = useState<string | undefined>();

  useEffect(() => {
    const fetchUserName = async () => {
      const userInfo = await AIProvider.userInfo;
      if (userInfo?.name) {
        const firstName = userInfo.name.split(' ')[0];
        setUserName(firstName);
      }
    };
    fetchUserName().catch(console.error);
  }, []);

  useEffect(() => {
    const handleStateChange = () => {
      setOnboardingStateVersion(prev => prev + 1);
    };
    window.addEventListener('storage', handleStateChange);
    window.addEventListener(
      'pique-onboarding-state-changed',
      handleStateChange
    );
    return () => {
      window.removeEventListener('storage', handleStateChange);
      window.removeEventListener(
        'pique-onboarding-state-changed',
        handleStateChange
      );
    };
  }, []);

  const filteredSteps = getPostAugmentationSteps();
  const currentStep = filteredSteps[stepIndex] || null;
  const currentStepId = currentStep?.id || null;

  useEffect(() => {
    if (isRunning && currentStepId) {
      document.body.dataset['onboardingStep'] = currentStepId;
    } else {
      delete document.body.dataset['onboardingStep'];
    }
    return () => {
      delete document.body.dataset['onboardingStep'];
    };
  }, [isRunning, currentStepId]);

  const checkMainOnboardingComplete = useCallback((): boolean => {
    const mainOnboardingShown = localStorage.getItem(
      STORAGE_KEYS.ONBOARDING_SHOWN
    );
    return mainOnboardingShown === 'true';
  }, []);

  const isMainFlowActive = useCallback((): boolean => {
    const inProgress = sessionStorage.getItem(
      STORAGE_KEYS.ONBOARDING_IN_PROGRESS
    );
    return (
      isMainTourRunning ||
      showSurvey ||
      inProgress === 'tour' ||
      inProgress === 'survey'
    );
  }, [isMainTourRunning, showSurvey]);

  const markPostAugmentationPending = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.POST_AUGMENTATION_PENDING, 'true');
    window.dispatchEvent(new Event('pique-onboarding-state-changed'));
  }, []);

  const clearPostAugmentationPending = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_PENDING);
    window.dispatchEvent(new Event('pique-onboarding-state-changed'));
  }, []);

  const openCompletionModal = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS, 'modal');
    setShowCompletionModal(true);
  }, []);

  const triggerPostAugmentationTour = useCallback(() => {
    if (isMobileViewValue) return;

    const alreadyShown = localStorage.getItem(
      STORAGE_KEYS.POST_AUGMENTATION_SHOWN
    );
    if (alreadyShown === 'true') return;

    if (isRunning || showCompletionModal || showFinalModal) return;

    if (!checkMainOnboardingComplete() || isMainFlowActive()) {
      markPostAugmentationPending();
      return;
    }

    clearPostAugmentationPending();
    openCompletionModal();
  }, [
    isMobileViewValue,
    checkMainOnboardingComplete,
    clearPostAugmentationPending,
    isMainFlowActive,
    isRunning,
    markPostAugmentationPending,
    openCompletionModal,
    showCompletionModal,
    showFinalModal,
  ]);

  const handleCompletionModalClose = useCallback(() => {
    setShowCompletionModal(false);
    clearPostAugmentationPending();
    sessionStorage.setItem(STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS, 'tour');
    setIsRunning(true);
  }, [clearPostAugmentationPending]);

  const handleCompletionModalSkip = useCallback(() => {
    setShowCompletionModal(false);
    clearPostAugmentationPending();
    setPersistedStepIndex(0);
    sessionStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS);

    const store = getCurrentStore();
    store.set(appSidebarOpenAtom, true as boolean | ((prev: boolean) => boolean));

    setTimeout(() => {
      const sidebarWidth = store.get(appSidebarWidthAtom);
      notify(
        {
          title: 'We saved your spot!',
          message: 'Resume the tour anytime from the sidebar.',
          icon: (
            <HugeiconsIcon
              icon={CircleArrowLeft02Icon}
              size={28}
              className="onboarding-notification-icon"
            />
          ),
          iconColor: 'var(--affine-primary-color)',
          style: 'normal',
          theme: 'info',
          rootAttrs: {
            style: {
              backgroundColor: 'var(--affine-background-primary-color)',
              color: 'var(--affine-text-primary-color)',
              borderColor: 'rgb(140, 92, 245)',
              boxShadow: 'var(--affine-menu-shadow)',
              borderRadius: '20px',
              border: '1px solid rgb(140, 92, 245)',
              padding: '16px 20px',
              fontSize: '15px',
            },
          },
        },
        {
          duration: NOTIFICATION_DURATION_MS,
          position: 'bottom-left',
          style: { marginLeft: `${sidebarWidth + 16}px`, marginBottom: '16px' },
        }
      );
      highlightTourButton(NOTIFICATION_DURATION_MS);
    }, 300);
  }, [clearPostAugmentationPending, setPersistedStepIndex]);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    setStepIndex(0);
    sessionStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS);
  }, []);

  const handleEarlyExit = useCallback(
    (currentStepIndex: number) => {
      setPersistedStepIndex(currentStepIndex);
      setIsRunning(false);
      sessionStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS);

      const store = getCurrentStore();
      store.set(appSidebarOpenAtom, true as boolean | ((prev: boolean) => boolean));

      setTimeout(() => {
        const sidebarWidth = store.get(appSidebarWidthAtom);
        notify(
          {
            title: 'We saved your spot!',
            message: 'Resume the tour anytime from the sidebar.',
            icon: (
              <HugeiconsIcon
                icon={CircleArrowLeft02Icon}
                size={28}
                className="onboarding-notification-icon"
              />
            ),
            iconColor: 'var(--affine-primary-color)',
            style: 'normal',
            theme: 'info',
            rootAttrs: {
              style: {
                backgroundColor: 'var(--affine-background-primary-color)',
                color: 'var(--affine-text-primary-color)',
                borderColor: 'rgb(140, 92, 245)',
                boxShadow: 'var(--affine-menu-shadow)',
                borderRadius: '20px',
                border: '1px solid rgb(140, 92, 245)',
                padding: '16px 20px',
                fontSize: '15px',
              },
            },
          },
          {
            duration: NOTIFICATION_DURATION_MS,
            position: 'bottom-left',
            style: {
              marginLeft: `${sidebarWidth + 16}px`,
              marginBottom: '16px',
            },
          }
        );
        highlightTourButton(NOTIFICATION_DURATION_MS);
      }, 300);
    },
    [setPersistedStepIndex]
  );

  useEffect(() => {
    if (!isRunning) return;
    const onOverlay = () => handleEarlyExit(stepIndexRef.current);
    document.addEventListener('pique-overlay-created', onOverlay);
    return () =>
      document.removeEventListener('pique-overlay-created', onOverlay);
  }, [isRunning, handleEarlyExit]);

  const resumePostAugmentationTour = useCallback(
    (fromStep?: number) => {
      if (isMobileViewValue) return;
      if (!checkMainOnboardingComplete()) return;
      if (isMainFlowActive()) return;
      const resumeStep = fromStep ?? persistedStepIndex;
      clearPostAugmentationPending();
      sessionStorage.setItem(
        STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS,
        'tour'
      );
      setIsRunning(true);
      setStepIndex(resumeStep);
    },
    [
      isMobileViewValue,
      checkMainOnboardingComplete,
      clearPostAugmentationPending,
      isMainFlowActive,
      persistedStepIndex,
    ]
  );

  const advanceToNextStep = useCallback(() => {
    const nextIndex = stepIndex + 1;
    if (nextIndex >= filteredSteps.length) {
      setPersistedStepIndex(0);
      stopTour();
      clearPostAugmentationPending();
      setTimeout(() => {
        setShowFinalModal(true);
      }, FINAL_MODAL_DELAY_MS);
    } else {
      const nextStepId = filteredSteps[nextIndex]?.id;
      const applyStep = () => {
        setStepIndex(nextIndex);
        setPersistedStepIndex(nextIndex);
        setIsRunning(true);
      };
      if (nextStepId === 'action-items-overview') {
        setTimeout(applyStep, ACTION_ITEMS_OVERVIEW_DELAY_MS);
        return;
      }
      applyStep();
    }
  }, [
    stepIndex,
    filteredSteps,
    stopTour,
    setPersistedStepIndex,
    clearPostAugmentationPending,
  ]);

  useEffect(() => {
    if (!isRunning || currentStepId !== 'action-items') return;

    let rafId: number;

    const pollDetailClosed = () => {
      const detail = document.querySelector('affine-data-view-record-detail');
      if (!detail) {
        advanceToNextStep();
        return;
      }
      rafId = requestAnimationFrame(pollDetailClosed);
    };

    const handleCardOpened = () => {
      setIsRunning(false);
      const waitForDetail = () => {
        const detail = document.querySelector('affine-data-view-record-detail');
        if (detail) {
          rafId = requestAnimationFrame(pollDetailClosed);
        } else {
          rafId = requestAnimationFrame(waitForDetail);
        }
      };
      setTimeout(waitForDetail, 600);
    };

    window.addEventListener('pique-kanban-card-opened', handleCardOpened);
    return () => {
      window.removeEventListener('pique-kanban-card-opened', handleCardOpened);
      cancelAnimationFrame(rafId);
    };
  }, [isRunning, currentStepId, advanceToNextStep]);


  useEffect(() => {
    if (!isRunning || currentStepId !== 'explore-mindmap') return;

    const timer = setTimeout(() => {
      const sidebarControls = document.querySelector(
        '.route-container_leftControls__1h9hyz64'
      );
      if (sidebarControls) {
        showProTipSlot.emit({
          id: 'mindmap-navigation-tip',
          targetElement: sidebarControls as HTMLElement,
          title: 'Hint!',
          content:
            '<strong>Pinch</strong> with two fingers to zoom<br/><strong>Glide</strong> two fingers on your trackpad to move around the mind map',
          placement: 'bottom-start',
          offset: 48,
          autoHideDelay: 12000,
          showCloseButton: true,
          icon: 'lightMode',
        });
      }
    }, 800);

    return () => {
      clearTimeout(timer);
      hideProTipSlot.emit('mindmap-navigation-tip');
    };
  }, [isRunning, currentStepId]);

  const handleFinalModalClose = useCallback(() => {
    setShowFinalModal(false);
    clearPostAugmentationPending();
    localStorage.setItem(STORAGE_KEYS.POST_AUGMENTATION_SHOWN, 'true');
    window.dispatchEvent(new Event('pique-onboarding-state-changed'));
    triggerPermissionsDock();
  }, [clearPostAugmentationPending, triggerPermissionsDock]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;

      if (status === STATUS.FINISHED) {
        setPersistedStepIndex(0);
        stopTour();
        clearPostAugmentationPending();
        localStorage.setItem(STORAGE_KEYS.POST_AUGMENTATION_SHOWN, 'true');
        window.dispatchEvent(new Event('pique-onboarding-state-changed'));
        return;
      }

      if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
        handleEarlyExit(index);
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextStepIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
        const currentStepIdLocal = filteredSteps[index]?.id;
        if (currentStepIdLocal === 'explore-mindmap') {
          hideProTipSlot.emit('mindmap-navigation-tip');
        }

        if (nextStepIndex >= filteredSteps.length) {
          setPersistedStepIndex(0);
          stopTour();
          clearPostAugmentationPending();
          setShowFinalModal(true);
        } else {
          const applyStep = () => {
            setStepIndex(nextStepIndex);
            setPersistedStepIndex(nextStepIndex);
          };
          applyStep();
        }
      } else if (type === EVENTS.TARGET_NOT_FOUND) {
        const nextIndex = index + 1;
        if (nextIndex >= filteredSteps.length) {
          setPersistedStepIndex(0);
          stopTour();
          clearPostAugmentationPending();
          setShowFinalModal(true);
        } else {
          setStepIndex(nextIndex);
          setPersistedStepIndex(nextIndex);
        }
      }
    },
    [
      stopTour,
      filteredSteps,
      handleEarlyExit,
      setPersistedStepIndex,
      clearPostAugmentationPending,
    ]
  );

  useEffect(() => {
    if (isMobileViewValue) return;

    const inProgress = sessionStorage.getItem(
      STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS
    );
    const mainOnboardingComplete =
      localStorage.getItem(STORAGE_KEYS.ONBOARDING_SHOWN) === 'true';
    const shouldResume = mainOnboardingComplete && !isMainFlowActive();
    if (inProgress === 'modal' && shouldResume) {
      setShowCompletionModal(true);
    } else if (inProgress === 'tour' && shouldResume) {
      setIsRunning(true);
      setStepIndex(persistedStepIndex);
    } else if (inProgress) {
      sessionStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS);
    }
  }, [isMobileViewValue, isMainFlowActive, persistedStepIndex]);

  useEffect(() => {
    if (isMobileViewValue) return;
    if (isRunning || showCompletionModal || showFinalModal) return;
    const pending =
      localStorage.getItem(STORAGE_KEYS.POST_AUGMENTATION_PENDING) === 'true';
    if (!pending) return;
    if (!checkMainOnboardingComplete() || isMainFlowActive()) return;

    clearPostAugmentationPending();
    openCompletionModal();
  }, [
    isMobileViewValue,
    onboardingStateVersion,
    isRunning,
    showCompletionModal,
    showFinalModal,
    checkMainOnboardingComplete,
    isMainFlowActive,
    clearPostAugmentationPending,
    openCompletionModal,
  ]);

  useEffect(() => {
    const mainOnboardingComplete =
      localStorage.getItem(STORAGE_KEYS.ONBOARDING_SHOWN) === 'true';
    if (mainOnboardingComplete && !isMainFlowActive()) return;

    if (showCompletionModal) {
      setShowCompletionModal(false);
    }
    if (showFinalModal) {
      setShowFinalModal(false);
    }
    if (isRunning) {
      setIsRunning(false);
    }
    setPersistedStepIndex(0);
    sessionStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS);
  }, [
    isMainFlowActive,
    isRunning,
    onboardingStateVersion,
    persistedStepIndex,
    setPersistedStepIndex,
    showCompletionModal,
    showFinalModal,
  ]);

  return (
    <PostAugmentationTourContext
      value={{
        showCompletionModal,
        triggerPostAugmentationTour,
        handleCompletionModalClose,
        isRunning,
        stepIndex,
        persistedStepIndex,
        resumePostAugmentationTour,
        handleJoyrideCallback,
        currentStepId,
        advanceToNextStep,
      }}
    >
      <CompletionModal
        isOpen={showCompletionModal}
        onClose={handleCompletionModalClose}
        onSkip={handleCompletionModalSkip}
      />
      <FinalCompletionModal
        isOpen={showFinalModal}
        onClose={handleFinalModalClose}
        userName={userName}
      />
      {isRunning && !showCompletionModal && (
        <Joyride
          {...DEFAULT_JOYRIDE_PROPS}
          steps={filteredSteps as Step[]}
          run={isRunning && !showCompletionModal}
          stepIndex={stepIndex}
          callback={handleJoyrideCallback}
          tooltipComponent={CustomTooltip}
          beaconComponent={CustomBeacon}
        />
      )}
      {children}
    </PostAugmentationTourContext>
  );
};
